import { CSSProperties, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "@entry/navigation";

import { Routes } from "@ctypes";
import { useEntryIntakeStore } from "@entry/store";
import { entryColors, entryMotion, prefersReducedMotion } from "@entry/tokens";
import { EntryTitle } from "@entry/ui";
import { Translations, i18n } from "@i18n";
import { Screen, useEntryPageAnalytics } from "./shared";

type GreetingStage = "hidden" | "visible" | "out";

const { easing, slideDistance, greetingHold, out } = entryMotion;

/**
 * Port of mobile PreferredNameScreen/GreetingContent: rise + fade in
 * (600/800ms), hold for 1s, fade out (400ms), then auto-advance.
 */
export const GreetingScreen = () => {
  const navigate = useNavigate();
  const firstName = useEntryIntakeStore(
    (state) => state.intake.preferredName || state.intake.firstName,
  );

  const [reduced] = useState(
    () =>
      prefersReducedMotion() ||
      (typeof document !== "undefined" && document.visibilityState === "hidden"),
  );
  const [stage, setStage] = useState<GreetingStage>(reduced ? "visible" : "hidden");
  const navigated = useRef(false);

  useEntryPageAnalytics(Routes.EntryGreeting);

  useEffect(() => {
    if (!firstName) return;

    const timers: number[] = [];
    const goNext = () => {
      if (navigated.current) return;
      navigated.current = true;
      navigate(Routes.EntryEmail);
    };

    if (reduced) {
      timers.push(window.setTimeout(goNext, greetingHold));
    } else {
      const raf = window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => setStage("visible")),
      );
      timers.push(
        window.setTimeout(() => setStage("out"), 800 + greetingHold),
        window.setTimeout(goNext, 800 + greetingHold + out + 20),
      );

      return () => {
        window.cancelAnimationFrame(raf);
        timers.forEach((t) => window.clearTimeout(t));
      };
    }

    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!firstName) {
    return <Navigate to={Routes.EntryName} replace />;
  }

  const style: CSSProperties = {
    opacity: stage === "visible" ? 1 : 0,
    transform: `translateY(${stage === "hidden" ? slideDistance : 0}px)`,
    transition: reduced
      ? "none"
      : `transform 600ms ${easing}, opacity ${stage === "out" ? out : 800}ms ${easing}`,
    textAlign: "center",
  };

  return (
    <Screen>
      <div style={style}>
        <EntryTitle as="p" $align="center" $size={40}>
          {i18n.t(Translations.EntryGreeting)}
          <br />
          <b style={{ color: entryColors.highlight }}>{firstName}.</b>
        </EntryTitle>
      </div>
    </Screen>
  );
};
